/**
 * App controller — state management + data-driven UI binders + render pipeline.
 *
 * Phase 4: Interactive editor.
 * Phase 5-7: Foil colors, artwork upload, config-driven init.
 * Phase 8: SVG download + edge case handling.
 *
 * Startup: fetch config.json → initState(config) → bindAll() → render()
 * Loop: UI events → setState(path, value) → debounced render()
 */

(function () {
  'use strict';

  // ── Runtime state (initialized from config.json) ──────
  let config = null;
  let state = null;
  let isOwnerMode = false;

  // ── Config loading ────────────────────────────────────

  // v0.42 slot builder — read per-slot values from config defaults
  // (fontFamily/fontWeight/fontSize/letterSpacing/lineSpacing/offsetY/
  // enabled/visible), falling back to typography.center + legacy hardcoded
  // offsets. Fixes the VC-005 session finding: initState used to ignore
  // everything but `.text`, so a config round-trip (export → config.js /
  // metafield → initState) silently lost slot fonts, sizes, offsets and
  // enabled toggles (visible as Courier Prime center text + re-enabled
  // toggles after refresh).
  function slotFromConfig(d, t, name, label, defaultOffsetY) {
    var slot = d.sources && d.sources.design && d.sources.design[name];
    var tc = t.center || {};
    return {
      enabled: slot && slot.enabled !== undefined ? slot.enabled : true,
      visible: slot && slot.visible !== undefined ? slot.visible : true,
      text: slot && slot.text !== undefined ? slot.text : '',
      fontFamily: (slot && slot.fontFamily) || tc.fontFamily || '',
      fontWeight: (slot && slot.fontWeight) || tc.fontWeight || '400',
      fontSize: (slot && slot.fontSize) || tc.fontSize || 120,
      letterSpacing: (slot && slot.letterSpacing) || tc.letterSpacing || 0,
      lineSpacing: (slot && slot.lineSpacing) || tc.lineSpacing || 1.2,
      textAlign: (slot && slot.textAlign) || 'center',
      offsetY: slot && slot.offsetY !== undefined ? slot.offsetY : defaultOffsetY,
      isEditable: true,
      label: label,
    };
  }

  function initState(cfg) {
    const d = cfg.defaults;
    const t = cfg.typography;
    const dg = d.sources && d.sources.design && d.sources.design.graphic;
    return {
      outerRing: {
        enabled: d.content.outerRing.enabled !== undefined ? d.content.outerRing.enabled : true,
        topText: d.content.outerRing.topText,
        bottomText: d.content.outerRing.bottomText,
        fontFamily: t.outerRing.fontFamily,
        fontWeight: t.outerRing.fontWeight || '400',
        topFontSize: t.outerRing.topFontSize || (t.outerRing.fontSize * (t.outerRing.topSizeRatio || 1.0)),
        bottomFontSize: t.outerRing.bottomFontSize || (t.outerRing.fontSize * (t.outerRing.bottomSizeRatio || 1.0)),
        letterSpacing: t.outerRing.letterSpacing,
        ringScale: d.ringScale.outer,
        trackRadius: d.trackRadius.outer,
        rotation: (d.rotation && d.rotation.outer) || 0,
        offsetY: 0, /* v0.41 — vertical shift */
      },
      innerRing: {
        enabled: d.content.innerRing.enabled !== undefined ? d.content.innerRing.enabled : true,
        topText: d.content.innerRing.topText,
        bottomText: d.content.innerRing.bottomText,
        fontFamily: t.innerRing.fontFamily,
        fontWeight: t.innerRing.fontWeight || '400',
        topFontSize: t.innerRing.topFontSize || (t.innerRing.fontSize * (t.innerRing.topSizeRatio || 1.0)),
        bottomFontSize: t.innerRing.bottomFontSize || (t.innerRing.fontSize * (t.innerRing.bottomSizeRatio || 1.0)),
        letterSpacing: t.innerRing.letterSpacing,
        ringScale: d.ringScale.inner,
        trackRadius: d.trackRadius.inner,
        rotation: (d.rotation && d.rotation.inner) || 0,
        offsetY: 0, /* v0.41 — vertical shift */
      },
      activeSourceId: d.activeSourceId || 'design',
      ui: {
        activeTab: 'design',
        previewOverride: null,
      },
      sources: {
        design: {
          centerThreshold: d.centerThreshold || 0.5,
          graphic: {
            enabled: dg && dg.enabled !== undefined ? dg.enabled : true,
            visible: dg && dg.visible !== undefined ? dg.visible : true,
            graphicId: (dg && dg.graphicId) || null,
            scale: (dg && dg.scale) || 1.0,
            offsetY: (dg && dg.offsetY) || 0,
            rotation: (dg && dg.rotation) || 0,
          },
          text1: slotFromConfig(d, t, 'text1', 'Line 1', -80),
          text2: slotFromConfig(d, t, 'text2', 'Line 2', 0),
          text3: slotFromConfig(d, t, 'text3', 'Line 3', 80),
        },
        upload: {
          imageDataUrl: null,
          scale: 1.0,
          threshold: 0.5,
          fitMode: 'meet',
        },
      },
      decoration: {
        baseSize: d.decorationBaseSize,
        graphic: d.decorationGraphic,
        show: true,
      },
      style: {
        paperColor: d.paperColor,
        ribbonColor: d.ribbonColor,
        printSafeBuffer: d.printSafeBuffer || 100,
        showTracks: true,
      },
      interactive: { foilShift: 0 },
      _config: cfg,
    };
  }

  // ── Legacy migration (v0.4 → v0.41) ────────────────────
  /**
   * Migrate v0.4 state to v0.41 structure.
   * Must NOT overwrite state.center.mode if already defined (full-bleed protection).
   * Populates text1/text2/text3 and offsetY fields from legacy flat fields.
   */
  function migrateLegacyState(s) {
    if (!s) return;

    // CASE 1: already v0.42 — idempotent, return
    if (s.sources && s.activeSourceId) return;

    // CASE 2: cold start (no center, no sources) — populate defaults
    if (!s.center && !s.sources) {
      s.activeSourceId = 'design';
      s.ui = { activeTab: 'design', previewOverride: null };
      s.sources = {
        design: {
          centerThreshold: 0.5,
          graphic: { visible: true, graphicId: null, scale: 1.0, offsetY: 0, rotation: 0 },
          text1: { visible: true, text: '', fontFamily: '', fontWeight: '400', fontSize: 120, letterSpacing: 0, lineSpacing: 1.2, offsetY: -80, isEditable: true, label: 'Line 1' },
          text2: { visible: true, text: '', fontFamily: '', fontWeight: '400', fontSize: 120, letterSpacing: 0, lineSpacing: 1.2, offsetY: 0, isEditable: true, label: 'Line 2' },
          text3: { visible: true, text: '', fontFamily: '', fontWeight: '400', fontSize: 120, letterSpacing: 0, lineSpacing: 1.2, offsetY: 80, isEditable: true, label: 'Line 3' },
        },
        upload: { imageDataUrl: null, scale: 1.0, threshold: 0.5, fitMode: 'meet' },
      };
      return;
    }

    // CASE 3: v0.41 legacy (has center, no sources) — migrate
    var cfg = s._config;
    var tc = cfg && cfg.typography ? cfg.typography.center : {};
    var mode = (s.center.mode === 'text' || s.center.mode === 'graphic') ? 'standard' : s.center.mode;
    s.activeSourceId = (mode === 'full-bleed') ? 'upload' : 'design';
    s.ui = { activeTab: 'design', previewOverride: null };
    s.sources = {
      design: {
        centerThreshold: s.center.centerThreshold || cfg && cfg.defaults && cfg.defaults.centerThreshold || 0.5,
        graphic: {
          visible: s.center.graphic && s.center.graphic.visible !== undefined ? s.center.graphic.visible : true,
          graphicId: s.center.graphic && s.center.graphic.graphicId || null,
          scale: s.center.graphic && s.center.graphic.scale || s.center.centerScale || 1.0,
          offsetY: s.center.graphic && s.center.graphic.offsetY || 0,
          rotation: s.center.graphic && s.center.graphic.rotation || 0,
        },
        text1: s.center.text1 ? {
          visible: s.center.text1.visible !== undefined ? s.center.text1.visible : true,
          text: s.center.text1.text || '',
          fontFamily: s.center.text1.fontFamily || tc.fontFamily || '',
          fontWeight: s.center.text1.fontWeight || tc.fontWeight || '400',
          fontSize: s.center.text1.fontSize || tc.fontSize || 120,
          letterSpacing: s.center.text1.letterSpacing || tc.letterSpacing || 0,
          lineSpacing: s.center.text1.lineSpacing || tc.lineSpacing || 1.2,
          offsetY: s.center.text1.offsetY !== undefined ? s.center.text1.offsetY : -80,
          isEditable: true,
          label: 'Line 1',
        } : { visible: true, text: '', fontFamily: tc.fontFamily || '', fontWeight: tc.fontWeight || '400', fontSize: tc.fontSize || 120, letterSpacing: tc.letterSpacing || 0, lineSpacing: tc.lineSpacing || 1.2, offsetY: -80, isEditable: true, label: 'Line 1' },
        text2: s.center.text2 ? {
          visible: s.center.text2.visible !== undefined ? s.center.text2.visible : true,
          text: s.center.text2.text || '',
          fontFamily: s.center.text2.fontFamily || tc.fontFamily || '',
          fontWeight: s.center.text2.fontWeight || tc.fontWeight || '400',
          fontSize: s.center.text2.fontSize || tc.fontSize || 120,
          letterSpacing: s.center.text2.letterSpacing || tc.letterSpacing || 0,
          lineSpacing: s.center.text2.lineSpacing || tc.lineSpacing || 1.2,
          offsetY: s.center.text2.offsetY !== undefined ? s.center.text2.offsetY : 0,
          isEditable: true,
          label: 'Line 2',
        } : { visible: true, text: '', fontFamily: tc.fontFamily || '', fontWeight: tc.fontWeight || '400', fontSize: tc.fontSize || 120, letterSpacing: tc.letterSpacing || 0, lineSpacing: tc.lineSpacing || 1.2, offsetY: 0, isEditable: true, label: 'Line 2' },
        text3: s.center.text3 ? {
          visible: s.center.text3.visible !== undefined ? s.center.text3.visible : true,
          text: s.center.text3.text || '',
          fontFamily: s.center.text3.fontFamily || tc.fontFamily || '',
          fontWeight: s.center.text3.fontWeight || tc.fontWeight || '400',
          fontSize: s.center.text3.fontSize || tc.fontSize || 120,
          letterSpacing: s.center.text3.letterSpacing || tc.letterSpacing || 0,
          lineSpacing: s.center.text3.lineSpacing || tc.lineSpacing || 1.2,
          offsetY: s.center.text3.offsetY !== undefined ? s.center.text3.offsetY : 80,
          isEditable: true,
          label: 'Line 3',
        } : { visible: true, text: '', fontFamily: tc.fontFamily || '', fontWeight: tc.fontWeight || '400', fontSize: tc.fontSize || 120, letterSpacing: tc.letterSpacing || 0, lineSpacing: tc.lineSpacing || 1.2, offsetY: 80, isEditable: true, label: 'Line 3' },
      },
      upload: {
        imageDataUrl: mode === 'full-bleed' && s.center.graphic && s.center.graphic.graphicId ? s.center.graphic.graphicId : null,
        scale: s.center.centerScale || 1.0,
        threshold: s.center.centerThreshold || 0.5,
        fitMode: s.center.fullBleedMode || 'meet',
      },
    };

    // Clean up legacy fields
    delete s.center;
    delete s._fullBleedPreview;
    delete s.centerMode;
  }

  // ── State helpers ─────────────────────────────────────
  function getState(path) {
    return path.split('.').reduce((obj, key) => obj && obj[key], state);
  }

  function setState(path, value) {
    const parts = path.split('.');
    const key = parts.pop();
    const obj = parts.reduce((o, p) => o[p], state);
    if (obj[key] === value) return;
    obj[key] = value;

    // Font-family or font-weight reactive reload: skip immediate render to
    // avoid FOUC, load the font asynchronously, then re-render with correct metrics.
    if (key === 'fontFamily' || key === 'fontWeight') {
      loadFontsForState(state);
      // Generic slot extraction via reduce — no path depth hardcoding (Q3)
      var pathParts = path.split('.');
      var slot = pathParts.slice(0, -1).reduce(function(o,k){return o&&o[k];}, state);
      var currentFamily = extractFontName(slot.fontFamily);
      var currentWeight = slot.fontWeight || '400';
      var loadSize = slot.topFontSize || slot.fontSize || 100;
      document.fonts.load(currentWeight + ' ' + loadSize + 'px "' + currentFamily + '"')
        .then(scheduleRender).catch(scheduleRender);
      // VC-011 Stage 2: register the TTF asset for the new family too, so
      // ring layout metrics exist before the re-render (fail-closed: an
      // unregistered family still throws a named-key Error at layout).
      var fm = window.StickerEngine && window.StickerEngine.fontMetrics;
      var assets = window.StickerFontAssets || {};
      var newKey = currentFamily + ':' + currentWeight;
      if (fm && assets[newKey]) {
        fm.loadFonts([{ family: currentFamily, weight: currentWeight, url: assets[newKey] }]);
      }
      return;
    }

    scheduleRender();
    if (path.indexOf('.enabled') !== -1) {
      updateConditionVisibility();
    }

    // Auto-show text3 when user interacts with its controls (visible=false in config)
    if (path.indexOf('sources.design.text3.') === 0 && key !== 'visible' && key !== 'label' && key !== 'isEditable') {
      if (state.sources.design.text3 && !state.sources.design.text3.visible) {
        state.sources.design.text3.visible = true;
      }
    }
  }

  // ── Debounced render ──────────────────────────────────
  let renderPending = false;

  function scheduleRender() {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function render() {
    const previewEl = document.getElementById('preview');
    if (!previewEl || !state) return;
    const computed = computeLayout(state);
    const svg = buildStickerSVG(state, computed);
    previewEl.innerHTML = '';
    previewEl.appendChild(svg);
  }

  // ── Condition visibility ──────────────────────────────
  function updateConditionVisibility() {
    document.querySelectorAll('[data-depends]').forEach(el => {
      const cond = el.getAttribute('data-depends');
      // Admin: skip hiding for lego toggles (enabled fields, admin must keep UI access)
      if (isOwnerMode && cond.indexOf('.enabled') !== -1) {
        return;
      }
      let visible = false;
      if (cond.includes('=')) {
        const [path, val] = cond.split('=');
        visible = String(getState(path)) === val;
      } else {
        visible = !!getState(cond);
      }
      el.classList.toggle('hidden', !visible);
    });
  }

  // ── Data-driven binder ────────────────────────────────
  function bindAll() {
    // Sliders
    document.querySelectorAll('[data-bind="slider"]').forEach(el => {
      const path = el.getAttribute('data-path');
      const display = document.getElementById(el.id + '-value');
      el.addEventListener('input', () => {
        const val = parseFloat(el.value);
        setState(path, val);
        if (display) display.textContent = formatValue(val);
      });
      const stepBtns = document.querySelectorAll(`.step-btn[data-for="${el.id}"]`);
      stepBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const dir = parseInt(btn.getAttribute('data-dir'));
          const step = parseFloat(el.step);
          const raw = parseFloat(el.value) + dir * step;
          const val = Math.max(parseFloat(el.min), Math.min(parseFloat(el.max), raw));
          el.value = val;
          setState(path, val);
          if (display) display.textContent = formatValue(val);
        });
      });
    });

    // Toggles
    document.querySelectorAll('[data-bind="toggle"]').forEach(el => {
      const path = el.getAttribute('data-path');
      el.addEventListener('change', () => setState(path, el.checked));
    });

    // Text inputs
    document.querySelectorAll('[data-bind="text"]').forEach(el => {
      const path = el.getAttribute('data-path');
      el.addEventListener('input', () => setState(path, el.value));
    });

    // Selects
    document.querySelectorAll('[data-bind="select"]').forEach(el => {
      const path = el.getAttribute('data-path');
      el.addEventListener('change', () => {
        setState(path, el.value);
        if (path.includes('Color')) scheduleRender();
      });
    });

    // Segmented buttons (VC-012-A alignment)
    document.querySelectorAll('[data-bind="segmented"]').forEach(el => {
      const path = el.getAttribute('data-path');
      el.addEventListener('click', () => {
        const value = el.getAttribute('data-value');
        setState(path, value);
        // VC-012-A fix: setState does not call syncStateToUI, so refresh the
        // group's .active highlight here (idempotent with syncStateToUI).
        el.parentElement.querySelectorAll('.seg-btn').forEach(b => {
          b.classList.toggle('active', b.getAttribute('data-value') === value);
        });
      });
    });

    // Checkboxes (L4c: lego toggles)
    document.querySelectorAll('[data-bind="checkbox"]').forEach(el => {
      const path = el.getAttribute('data-path');
      el.checked = getState(path) !== false;
      el.addEventListener('change', () => {
        setState(path, el.checked);
        updateConditionVisibility();
        render();
      });
    });

    // Upload
    const uploadInput = document.getElementById('upload-artwork');
    const uploadArea = document.getElementById('upload-area');
    if (uploadInput && uploadArea) {
      uploadArea.addEventListener('click', () => uploadInput.click());
      uploadInput.addEventListener('change', handleUpload);
    }

    // Revert
    const revertBtn = document.getElementById('revert-artwork');
    if (revertBtn) revertBtn.addEventListener('click', handleRevert);

    // Add to Cart (checkout)
    const checkoutBtn = document.getElementById('add-to-cart');
    if (checkoutBtn) checkoutBtn.addEventListener('click', handleCheckout);

    // Interactive Sheen
    initDynamicFoil();
  }

  // ── Tab Switching (visual only — no state mutation) ────
  function bindTabs() {
    var tabs = document.querySelectorAll('.tab-btn');
    if (!tabs.length) return;

    tabs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var tab = btn.getAttribute('data-tab');
        if (!tab) return;

        var prevTab = document.querySelector('.tab-btn.active');
        var prevTabName = prevTab ? prevTab.getAttribute('data-tab') : null;

        // Leaving Upload tab → clear preview override, sync UI
        if (state && prevTabName === 'upload' && tab !== 'upload') {
          state.ui.previewOverride = null;
        }

        // Deactivate all tabs
        tabs.forEach(function (t) { t.classList.remove('active'); });
        btn.classList.add('active');

        // Deactivate all panels, activate selected
        document.querySelectorAll('[data-tab-panel]').forEach(function (p) {
          p.classList.remove('active');
        });
        var panel = document.querySelector('[data-tab-panel="' + tab + '"]');
        if (panel) panel.classList.add('active');

        // Track active tab for editable-fields (L4b: tab scoping uses state.ui.activeTab)
        if (state && state.ui) state.ui.activeTab = tab;

        // Re-run field filter for new tab (L4b: tab scoping)
        applyEditableFieldsFilter();

        // Entering Upload tab → set preview override to upload, sync slider positions
        if (state && tab === 'upload') {
          state.ui.previewOverride = 'upload';
          syncStateToUI();
          scheduleRender();
        } else if (state) {
          // Leaving Upload → clear preview override, sync UI then re-render
          state.ui.previewOverride = null;
          syncStateToUI();
          scheduleRender();
        }
      });
    });
  }

  // ── Full-Bleed Upload State Management ──────────────────
  function initFullBleed() {
    var uploadInput = document.getElementById('full-bleed-upload');
    var uploadArea = document.getElementById('full-bleed-upload-area');
    if (uploadInput && uploadArea) {
      uploadArea.addEventListener('click', function () { uploadInput.click(); });
      uploadInput.addEventListener('change', handleFullBleedUpload);
    }

    // Threshold slider for full-bleed masking — handled by data-bind/data-path in bindAll + syncStateToUI

    // Fit/Fill/Remove buttons
    bindFullBleedActions();
  }

  function bindFullBleedActions() {
    var fitBtn = document.getElementById('fb-btn-fit');
    var fillBtn = document.getElementById('fb-btn-fill');
    var removeBtn = document.getElementById('fb-btn-remove');

    if (fitBtn) fitBtn.addEventListener('click', function () {
      if (!state) return;
      state.sources.upload.fitMode = 'meet';
      fitBtn.classList.add('active');
      if (fillBtn) fillBtn.classList.remove('active');
      scheduleRender();
    });

    if (fillBtn) fillBtn.addEventListener('click', function () {
      if (!state) return;
      state.sources.upload.fitMode = 'slice';
      fillBtn.classList.add('active');
      if (fitBtn) fitBtn.classList.remove('active');
      scheduleRender();
    });

    if (removeBtn) removeBtn.addEventListener('click', function () {
      if (!state) return;
      // v0.41 — reset structured graphic object
      state.sources.upload = { imageDataUrl: null, scale: 1.0, threshold: 0.5, fitMode: 'meet' };
      state.activeSourceId = 'design';
      state.ui.previewOverride = null;
      syncStateToUI(); // snaps scale and threshold sliders back to defaults
      var actions = document.getElementById('full-bleed-actions');
      if (actions) actions.style.display = 'none';
      var statusEl = document.getElementById('full-bleed-upload-status');
      if (statusEl) {
        statusEl.textContent = 'Design removed.';
        statusEl.className = 'upload-status';
      }
      scheduleRender();
    });
  }

  function handleFullBleedUpload(e) {
    var file = e.target.files && e.target.files[0];
    var statusEl = document.getElementById('full-bleed-upload-status');
    if (!file || !statusEl) return;

    if (file.size > 5 * 1024 * 1024) {
      statusEl.textContent = 'File too large. Maximum size is 5MB.';
      statusEl.className = 'upload-status error';
      return;
    }

    statusEl.textContent = 'Reading file...';
    statusEl.className = 'upload-status';

    var reader = new FileReader();
    reader.onload = function (ev) {
      if (!state) return;
      // v0.41 — structured graphic object
      state.sources.upload.imageDataUrl = ev.target.result;
      state.activeSourceId = 'upload';
      state.ui.previewOverride = null;
      // Show Fit/Fill/Remove actions
      var actions = document.getElementById('full-bleed-actions');
      if (actions) actions.style.display = 'block';
      statusEl.textContent = 'Design applied.';
      statusEl.className = 'upload-status success';
      scheduleRender();
    };
    reader.onerror = function () {
      statusEl.textContent = "Couldn't read file. Please try again.";
      statusEl.className = 'upload-status error';
    };
    reader.readAsDataURL(file);
  }

  // ── Dynamic Foil Sheen ────────────────────────────────
  // VC-010-D (advisor model, 2026-08-06): window-level pointer tracking
  // relative to the sticker center; rAF inertia smoothing; no reset on
  // preview-boundary exit; only pointer-leave/browser-blur eases back to 0.
  function initDynamicFoil() {
    const previewContainer = document.getElementById('preview-container');
    if (!previewContainer) return;

    // Gradient stop positions for a given shift (advisor models:
    // 5-stop symmetric band — stops 1/2/3 move together, ±12 travel;
    // 3-stop — [0, p−4, p+4] rollback point).
    function applyFoilShift(id, shift) {
      const gradEl = document.getElementById(id);
      if (!gradEl) return;
      const stops = gradEl.querySelectorAll('stop');
      if (stops.length === 5 && stops[1] && stops[2] && stops[3] && stops[4]) {
        stops[1].setAttribute('offset', `${40 + shift}%`);
        stops[2].setAttribute('offset', `${50 + shift}%`);
        stops[3].setAttribute('offset', `${60 + shift}%`);
        // stop 0 and 4 fixed (environment anchors), never moved
      } else if (stops.length === 3 && stops[1] && stops[2]) {
        stops[1].setAttribute('offset', `${56 + shift}%`);
        stops[2].setAttribute('offset', `${64 + shift}%`);
      }
    }

    let targetShift = 12;
    let currentShift = 0;
    let pointerActive = false;
    let rafId = null;

    function tick() {
      currentShift += (targetShift - currentShift) * 0.12;
      if (Math.abs(targetShift - currentShift) < 0.05) currentShift = targetShift;
      if (state && state.style.ribbonColor && state.style.ribbonColor.includes('foil')) {
        state.interactive.foilShift = currentShift;
        applyFoilShift(state.style.ribbonColor, currentShift);
      }
      if (pointerActive || Math.abs(targetShift - currentShift) > 0.05) {
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    }

    function schedule() {
      if (!rafId) rafId = requestAnimationFrame(tick);
    }

    // Diagonal projection of the pointer relative to the sticker center.
    // (dx/width + dy/height) ∈ [−1, 1] → ×12, clamped to ±12: at any
    // corner the light faces keep bright + highlight + dark (advisor v2).
    window.addEventListener('pointermove', (e) => {
      pointerActive = true;
      const rect = previewContainer.getBoundingClientRect();
      const dx = (e.clientX - (rect.left + rect.width / 2)) / rect.width;
      const dy = (e.clientY - (rect.top + rect.height / 2)) / rect.height;
      targetShift = Math.max(-12, Math.min(12, (dx + dy) * 12));
      schedule();
    });

    // Leaving the browser window (or blur) parks the light band at the
    // extreme so it never rests on the center text.
    document.addEventListener('pointerleave', () => {
      pointerActive = false;
      targetShift = 12;
      schedule();
    });
    window.addEventListener('blur', () => {
      pointerActive = false;
      targetShift = 12;
      schedule();
    });
  }

  // ── Upload handling ───────────────────────────────────
  const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];

  function handleUpload(e) {
    const file = e.target.files && e.target.files[0];
    const statusEl = document.getElementById('upload-status');
    if (!file || !statusEl) return;

    // Validate
    if (!ALLOWED_TYPES.includes(file.type)) {
      statusEl.textContent = 'Unsupported format. Please use PNG, JPG, or SVG.';
      statusEl.className = 'upload-status error';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      statusEl.textContent = 'File too large. Maximum size is 5MB.';
      statusEl.className = 'upload-status error';
      return;
    }

    statusEl.textContent = 'Reading file...';
    statusEl.className = 'upload-status';

    const reader = new FileReader();
    reader.onload = (ev) => {
      // v0.41 — structured graphic object
      state.sources.design.graphic.graphicId = ev.target.result;
      state.sources.design.graphic.visible = true;
      // VC-012-B S1 fix: uploading a graphic implies showing it — the render
      // gate requires enabled !== false, and the default is false.
      state.sources.design.graphic.enabled = true;
      syncStateToUI(); // VC-012-B: checkbox must follow the new enabled state
      statusEl.textContent = 'Image applied.';
      statusEl.className = 'upload-status success';
      scheduleRender();
    };
    reader.onerror = () => {
      statusEl.textContent = "Couldn't read file. Please try again.";
      statusEl.className = 'upload-status error';
    };
    reader.readAsDataURL(file);
  }

  function handleRevert() {
    const statusEl = document.getElementById('upload-status');
    // v0.41 — reset structured graphic object (VC-012-B S4: keep the Show
    // toggle state, reset rotation explicitly)
    state.sources.design.graphic = { visible: true, graphicId: null, scale: 1.0, offsetY: 0, rotation: 0, enabled: state.sources.design.graphic.enabled !== false };
    syncStateToUI(); // VC-012-B: rotation slider + Show checkbox must follow
    if (statusEl) {
      statusEl.textContent = 'Image removed.';
      statusEl.className = 'upload-status';
    }
    scheduleRender();
  }

  // ── Checkout (Mock) ──────────────────────────────────────
  function handleCheckout() {
    const prevTracks = state.style.showTracks;
    state.style.showTracks = false;

    // Generate clean SVG for upload
    const cleanSvg = window.buildStickerSVG(state, window.computeLayout(state));
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(cleanSvg);

    state.style.showTracks = prevTracks;

    // Mock: log payload size
    console.log("Mock: Sending payload to Cloudflare Worker...", cleanSvg.length);

    // Create downloadable blob (development convenience)
    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sticker.svg';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // User-facing alert
    alert("Checkout initiated. (In production, this uploads files to Shopify CDN and adds to cart).");
  }

  // ── Config Export (Admin) ───────────────────────────────
  function exportMergedConfig() {
    var cfg = JSON.parse(JSON.stringify(window.StickerConfig));
    var s = state;

    // Sources — serialize current state into defaults
    cfg.defaults.sources = JSON.parse(JSON.stringify(s.sources));
    cfg.defaults.sources.upload.imageDataUrl = null;  // runtime data, not default (T3)

    // Active source & UI
    cfg.defaults.activeSourceId = s.activeSourceId;
    cfg.defaults.ui = JSON.parse(JSON.stringify(s.ui));

    // Ring scale — center → sources.upload.scale
    cfg.defaults.ringScale.outer = s.outerRing.ringScale;
    cfg.defaults.ringScale.inner = s.innerRing.ringScale;
    delete cfg.defaults.ringScale.center;

    // Content (texts + ring enabled flags — flags were previously lost on
    // round-trip: initState hardcoded enabled:true; VC-005 acceptance fix)
    cfg.defaults.content.outerRing.enabled = s.outerRing.enabled;
    cfg.defaults.content.innerRing.enabled = s.innerRing.enabled;
    cfg.defaults.content.outerRing.topText = s.outerRing.topText;
    cfg.defaults.content.outerRing.bottomText = s.outerRing.bottomText;
    cfg.defaults.content.innerRing.topText = s.innerRing.topText;
    cfg.defaults.content.innerRing.bottomText = s.innerRing.bottomText;

    // Rotation
    cfg.defaults.rotation.outer = s.outerRing.rotation;
    cfg.defaults.rotation.inner = s.innerRing.rotation;

    // Track radius
    cfg.defaults.trackRadius.outer = s.outerRing.trackRadius;
    cfg.defaults.trackRadius.inner = s.innerRing.trackRadius;

    // Colors
    cfg.defaults.paperColor = s.style.paperColor;
    cfg.defaults.ribbonColor = s.style.ribbonColor;

    // Print safety
    cfg.defaults.printSafeBuffer = s.style.printSafeBuffer;

    // Decoration
    cfg.defaults.decorationBaseSize = s.decoration.baseSize;
    cfg.defaults.decorationGraphic = s.decoration.show === false ? null : s.decoration.graphic;

    // Typography — Outer ring
    cfg.typography.outerRing.fontFamily = s.outerRing.fontFamily;
    cfg.typography.outerRing.topFontSize = s.outerRing.topFontSize;
    cfg.typography.outerRing.bottomFontSize = s.outerRing.bottomFontSize;
    cfg.typography.outerRing.fontWeight = s.outerRing.fontWeight;
    cfg.typography.outerRing.letterSpacing = s.outerRing.letterSpacing;

    // Typography — Inner ring
    cfg.typography.innerRing.fontFamily = s.innerRing.fontFamily;
    cfg.typography.innerRing.topFontSize = s.innerRing.topFontSize;
    cfg.typography.innerRing.bottomFontSize = s.innerRing.bottomFontSize;
    cfg.typography.innerRing.fontWeight = s.innerRing.fontWeight;
    cfg.typography.innerRing.letterSpacing = s.innerRing.letterSpacing;

    return JSON.stringify(cfg, null, 2);
  }

  // ── Slot slider ranges from config ────────────────────
  function applySliderRanges(cfg) {
    const rangeMap = {
      'outerRing.topFontSize': t => t.outerRing.fontSizeRange,
      'outerRing.bottomFontSize': t => t.outerRing.fontSizeRange,
      'innerRing.topFontSize': t => t.innerRing.fontSizeRange,
      'innerRing.bottomFontSize': t => t.innerRing.fontSizeRange,
      'outerRing.letterSpacing': t => t.outerRing.letterSpacingRange,
      'innerRing.letterSpacing': t => t.innerRing.letterSpacingRange,
      /* v0.42 — per-slot text ranges (sources.design.*) */
      'sources.design.text1.fontSize': t => t.center.fontSizeRange,
      'sources.design.text1.letterSpacing': t => t.center.letterSpacingRange,
      'sources.design.text1.lineSpacing': t => t.center.lineSpacingRange,
      'sources.design.text2.fontSize': t => t.center.fontSizeRange,
      'sources.design.text2.letterSpacing': t => t.center.letterSpacingRange,
      'sources.design.text2.lineSpacing': t => t.center.lineSpacingRange,
      'sources.design.text3.fontSize': t => t.center.fontSizeRange,
      'sources.design.text3.letterSpacing': t => t.center.letterSpacingRange,
      'sources.design.text3.lineSpacing': t => t.center.lineSpacingRange,
    };
    Object.entries(rangeMap).forEach(([path, getRange]) => {
      const [min, max] = getRange(cfg.typography);
      const el = document.querySelector(`[data-path="${path}"]`);
      if (el) { el.min = min; el.max = max; }
    });
    // Scale sliders from config
    const scaleSliders = {
      'outerRing.ringScale': cfg.sliderRanges.outerRingScale,
      'innerRing.ringScale': cfg.sliderRanges.innerRingScale,
      'sources.design.graphic.scale': cfg.sliderRanges['sources.design.graphic.scale'],
      'sources.design.centerThreshold': cfg.sliderRanges['sources.design.centerThreshold'],
      'sources.upload.threshold': cfg.sliderRanges['sources.upload.threshold'],
      'sources.upload.scale': cfg.sliderRanges['sources.upload.scale'],
      /* v0.42 — Y-offset ranges */
      'outerRing.offsetY': cfg.sliderRanges.outerRingOffsetY,
      'innerRing.offsetY': cfg.sliderRanges.innerRingOffsetY,
      'sources.design.graphic.offsetY': cfg.sliderRanges['sources.design.graphic.offsetY'],
      'sources.design.graphic.rotation': cfg.sliderRanges['sources.design.graphic.rotation'],
      'sources.design.text1.offsetY': cfg.sliderRanges['sources.design.text1.offsetY'],
      'sources.design.text2.offsetY': cfg.sliderRanges['sources.design.text2.offsetY'],
      'sources.design.text3.offsetY': cfg.sliderRanges['sources.design.text3.offsetY'],
    };
    Object.entries(scaleSliders).forEach(([path, r]) => {
      const el = document.querySelector(`[data-path="${path}"]`);
      // VC-012-C fix: a config missing the range for a NEW slider must not
      // crash the boot (the store metafield predates rotation; the slider
      // exists in the DOM, r is undefined) — fall back to the markup attrs.
      if (el && r) { el.min = r.min; el.max = r.max; el.step = r.step; }
    });
  }

  // ── Populate color selects from palette ───────────────
  function populateSelect(id, palette, current) {
    const sel = document.getElementById(id);
    if (!sel) return;
    Object.keys(palette).forEach(key => {
      const entry = palette[key];
      const opt = document.createElement('option');
      opt.value = key;
      opt.textContent = entry.label || key;
      if (key === current) opt.selected = true;
      sel.appendChild(opt);
    });
  }

  // ── Sync state → UI ───────────────────────────────────
  function syncStateToUI() {
    document.querySelectorAll('[data-bind="slider"]').forEach(el => {
      const path = el.getAttribute('data-path');
      const val = getState(path);
      if (val !== undefined) {
        el.value = val;
        const display = document.getElementById(el.id + '-value');
        if (display) display.textContent = formatValue(val);
      }
    });
    document.querySelectorAll('[data-bind="toggle"]').forEach(el => {
      el.checked = !!getState(el.getAttribute('data-path'));
    });
    document.querySelectorAll('[data-bind="checkbox"]').forEach(el => {
      el.checked = !!getState(el.getAttribute('data-path'));
    });
    document.querySelectorAll('[data-bind="text"]').forEach(el => {
      const val = getState(el.getAttribute('data-path'));
      if (val !== undefined) el.value = val;
    });
    document.querySelectorAll('[data-bind="select"]').forEach(el => {
      const val = getState(el.getAttribute('data-path'));
      if (val !== undefined && Array.from(el.options).some(o => o.value === val)) {
        el.value = val;
      }
    });
    document.querySelectorAll('[data-bind="segmented"]').forEach(el => {
      const val = getState(el.getAttribute('data-path'));
      el.classList.toggle('active', el.getAttribute('data-value') === val);
    });
    updateConditionVisibility();
  }

  function formatValue(val) {
    return Number.isInteger(val) ? val.toString() : val.toFixed(2);
  }

  // ── Config whitelist filtering ───────────────────────────
  function applyEditableFieldsFilter() {
    var controlsEl = document.getElementById('controls-column');
    if (!controlsEl) return;

    // Owner mode: unlock container and skip filtering
    if (isOwnerMode) {
      controlsEl.classList.remove('locked-by-config');
      return;
    }

    var whitelist = window.StickerConfig.editableFields;
    if (!whitelist || typeof whitelist !== 'object' || Array.isArray(whitelist)) {
      // Not an object = legacy array or missing = hide nothing for backward compat
      controlsEl.classList.remove('locked-by-config');
      return;
    }

    // 0. Clean up previous locks before re-evaluating for the current tab
    document.querySelectorAll('.locked-by-config').forEach(function (el) {
      el.classList.remove('locked-by-config');
    });

    // 1. Hide control groups not allowed in current tab
    //    (editableFields 从数组改为对象, 每个字段带 allowedTabs)
    //    admin mode (isOwnerMode) 在上面已 return, 走不到这里
    var currentTab = state && state.ui ? state.ui.activeTab : 'design';
    document.querySelectorAll('[data-path]').forEach(function (el) {
      var path = el.getAttribute('data-path');
      var rule = whitelist[path];
      if (rule && rule.allowedTabs.indexOf(currentTab) !== -1) {
        return; // 允许在当前 tab 显
      }
      var group = el.closest('.control-group');
      if (group) group.classList.add('locked-by-config');
    });

    // 2. Orphan step-button protection (data-for)
    document.querySelectorAll('.step-btn[data-for]').forEach(function (btn) {
      var targetId = btn.getAttribute('data-for');
      var target = document.getElementById(targetId);
      if (!target) return;
      if (target.closest('.locked-by-config')) {
        var btnGroup = btn.closest('.control-group');
        if (btnGroup) btnGroup.classList.add('locked-by-config');
      }
    });

    // 3. Empty section handling
    document.querySelectorAll('[data-section]').forEach(function (section) {
      var groups = section.querySelectorAll('.control-group');
      if (groups.length === 0) return; // no groups to judge
      var allLocked = Array.from(groups).every(function (g) {
        return g.classList.contains('locked-by-config');
      });
      if (allLocked) section.classList.add('locked-by-config');
    });

    // 4. Unlock the container now that filtering is done
    controlsEl.classList.remove('locked-by-config');
  }

  // ── Font metrics assets (VC-011 Stage 2, Layer 1) ─────
  // Register the TTF assets the current state needs with the font
  // metrics module and wait for them. Fail-closed (WO §4, Advisor ruling
  // Q4): a missing/unloadable font ASSET is a visible error — never a
  // silent fallback, never a partial artifact, alert() is not a mechanism.
  async function ensureFontMetrics(state) {
    var fm = window.StickerEngine && window.StickerEngine.fontMetrics;
    if (!fm) return [];
    var assets = window.StickerFontAssets || {};
    var specs = [];
    var items = [
      { family: state.outerRing.fontFamily, weight: state.outerRing.fontWeight || '400' },
      { family: state.innerRing.fontFamily, weight: state.innerRing.fontWeight || '400' },
    ];
    ['text1', 'text2', 'text3'].forEach(function (k) {
      var s = state.sources && state.sources.design && state.sources.design[k];
      if (s && s.fontFamily) items.push({ family: s.fontFamily, weight: s.fontWeight || '400' });
    });
    items.forEach(function (it) {
      var name = extractFontName(it.family);
      if (!name) return;
      var key = name + ':' + (it.weight || '400');
      var url = assets[key];
      if (url) specs.push({ family: name, weight: it.weight || '400', url: url });
    });
    return fm.loadFonts(specs);
  }

  // Visible error state in the editor preview (Q4); the throw itself is
  // the mechanism, this only makes it legible on screen and in console.
  function showFontError(failures) {
    var previewEl = document.getElementById('preview');
    var msg = 'Font asset unavailable: ' + failures.join(', ') +
      ' — check StickerFontAssets in index.html.';
    console.error('[sticker-engine] ' + msg);
    if (previewEl) {
      previewEl.innerHTML = '<div style="padding:24px;color:#b00000;font:14px -apple-system,sans-serif;white-space:pre-wrap">' + msg + '</div>';
    }
  }

  // ── Dynamic Font Loader ─────────────────────────────────
  var _injectedFonts = {};

  /**
   * Extract raw font name from a CSS font-family string.
   * "'Open Sans', sans-serif" → "Open Sans"
   */
  function extractFontName(fontFamily) {
    var name = fontFamily.replace(/['"]/g, '').split(',')[0].trim();
    return name;
  }

  /**
   * Dynamically inject Google Fonts <link> tags from runtime state.
   * Scans outerRing, innerRing, and per-slot center text1/text2/text3
   * for their fontFamily/fontWeight, injecting each unique font once.
   * Safe to call repeatedly — _injectedFonts dedup prevents duplicates.
   */
  function loadFontsForState(state) {
    var items = [
      { family: state.outerRing.fontFamily, weight: state.outerRing.fontWeight || '400' },
      { family: state.innerRing.fontFamily, weight: state.innerRing.fontWeight || '400' },
    ];
    ['text1', 'text2', 'text3'].forEach(function (k) {
      var s = state.sources.design[k];
      if (s && s.fontFamily) items.push({ family: s.fontFamily, weight: s.fontWeight || '400' });
    });

    items.forEach(function (item) {
      var name = extractFontName(item.family);
      if (!name) return;
      var fontKey = name + ':' + (item.weight || '400');
      if (_injectedFonts[fontKey]) return;
      _injectedFonts[fontKey] = true;

      // Skip if already present in document from another source
      var searchHref = name.replace(/\s+/g, '+') + ':wght@' + (item.weight || '400');
      if (document.querySelector('link[href*="' + searchHref + '"]')) return;

      var link = document.createElement('link');
      link.rel = 'stylesheet';
      var url = 'https://fonts.googleapis.com/css2?family=' + encodeURIComponent(name);
      if (item.weight) {
        url += ':wght@' + item.weight;
      }
      url += '&display=swap';
      link.href = url;
      document.head.appendChild(link);
    });
  }

  // ── Bootstrap ─────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', async () => {
    config = window.StickerConfig;
    if (!config) {
      return;
    }

    state = initState(config);

    // v0.41 — migrate any legacy state fields, with full-bleed protection
    migrateLegacyState(state);

    // Dynamic font injection — loads before render so layout gets correct metrics
    loadFontsForState(state);

    // VC-011 Stage 2: font metrics assets (Layer 1) — fail-closed.
    // A missing asset makes the first render throw below, which shows the
    // visible error state (showFontError) — never a silent partial preview.
    await ensureFontMetrics(state).catch(function (e) {
      console.error('[sticker-engine] ' + String((e && e.message) || e));
    });

    // Populate color selects from palette
    populateSelect('paper-color', config.palette.paper, state.style.paperColor);
    populateSelect('ribbon-color', config.palette.ribbon, state.style.ribbonColor);

    // Apply slider ranges from config
    applySliderRanges(config);

    // Admin mode detection (needed before bindAll so generateAdminControls() runs first)
    isOwnerMode = window.location.search.indexOf('mode=admin') !== -1
      || window.location.hash === '#admin'
      || localStorage.getItem('sticker_admin') === 'true';
    if (isOwnerMode) document.body.classList.add('admin-mode');

    // Bind all UI controls
    bindAll();

    // Bind tab switching (activates after Phase 1 filter unlocks container)
    bindTabs();

    // Bind full-bleed upload (no data-path — outside whitelist)
    initFullBleed();

    // Design Revert to Default
    var revertDesignBtn = document.getElementById('revert-design');
    if (revertDesignBtn) {
      revertDesignBtn.addEventListener('click', function () {
        if (!config || !state) return;
        state = initState(config);
        populateSelect('paper-color', config.palette.paper, state.style.paperColor);
        populateSelect('ribbon-color', config.palette.ribbon, state.style.ribbonColor);
        syncStateToUI();
        scheduleRender();
      });
    }

    // Copy Config JSON (admin export)
    var exportBtn = document.getElementById('export-state-btn');
    if (exportBtn) {
      if (isOwnerMode) exportBtn.style.display = '';
      exportBtn.addEventListener('click', function () {
        var json = exportMergedConfig();
        navigator.clipboard.writeText(json).then(function () {
          var orig = exportBtn.textContent;
          exportBtn.textContent = 'Copied!';
          setTimeout(function () { exportBtn.textContent = orig; }, 2000);
        });
      });
    }

    // Paste Config (admin import) — VC-012-PRE: load a metafield/exported
    // StickerConfig JSON into the current design. Transient preview only —
    // never writes config.js; Revert to Default still resets to file defaults.
    var importArea = document.getElementById('import-state-area');
    var importBtn = document.getElementById('load-config-btn');
    if (importArea && importBtn) {
      if (isOwnerMode) {
        importArea.style.display = '';
        importBtn.style.display = '';
      }
      importBtn.addEventListener('click', function () {
        var raw = (importArea.value || '').trim();
        if (!raw) { alert('Paste a config JSON first.'); return; }
        var parsed;
        try { parsed = JSON.parse(raw); }
        catch (e) { alert('Config JSON parse error: ' + e.message); return; }
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          alert('Config JSON must be an object.'); return;
        }
        try {
          state = initState(parsed);
          populateSelect('paper-color', parsed.palette.paper, state.style.paperColor);
          populateSelect('ribbon-color', parsed.palette.ribbon, state.style.ribbonColor);
          applySliderRanges(parsed);
          syncStateToUI();
          scheduleRender();
        } catch (e) {
          alert('Config load failed: ' + ((e && e.message) || e));
        }
      });
    }

    // Initial sync: state → UI
    syncStateToUI();

    // Apply editable fields filter
    applyEditableFieldsFilter();

    // FOUC reveal: container hidden via .fouc-hide at load, reveal after whitelist pruning
    var controlsEl = document.getElementById('controls-column');
    if (controlsEl) {
      controlsEl.classList.remove('fouc-hide');
      controlsEl.classList.add('fouc-ready');
    }

    // Font synchronization barrier — prevent Canvas 2D measurement race conditions
    if (document.fonts && document.fonts.ready) {
      try {
        await Promise.race([
          document.fonts.ready,
          new Promise(function (r) { setTimeout(r, 500); }),
        ]);
      } catch (e) {
        // Timeout or error — proceed with fallback fonts
      }
    }

    // First render (fonts are now ready). Fail-closed: a font-asset
    // error is shown visibly, never a silent partial preview.
    try {
      render();
    } catch (e) {
      showFontError([String((e && e.message) || e)]);
    }
  });

  // Expose state/config for engine-api wrappers
  window.StickerEngine = window.StickerEngine || {};
  window.StickerEngine._getState = () => state; // readonly
  window.StickerEngine._getConfig = () => config;
  window.StickerEngine.computeLayout = computeLayout;
  window.StickerEngine.buildStickerSVG = buildStickerSVG;

})();
